import { useState, useCallback, useMemo, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { PaymentRequest, PaymentRequestPaymentMethodEvent, Stripe } from '@stripe/stripe-js';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { apiStore } from '@/stores/ApiStore';
import { config } from '@/config/env';
import { getErrorMessage } from './usePaymentErrors';
import {
  sendPaymentFailed,
  sendHandoff,
  sendPaymentSuccess,
  isInWebView,
} from './useWebViewMessaging';
import { buildAppsFlyerDeeplinkFromPath, buildAppsFlyerRedirectUrl } from '@/utils/deeplink';
import type { PlanItem } from '@/types/api';
import { consumeAppsFlyerParams } from '@/utils/appsFlyerParams';
import {
  buildAttConversionClickPayload,
  getStoredAttParams,
  resolveAttributionEmail,
  setConversionClickSent,
  wasConversionClickSent,
} from '@/utils/attParams';
import { paymentConfirmationStore } from '@/stores/PaymentConfirmationStore';
import { navigateWithPaymentOverlay } from '@/utils/paymentNavigation';
import { trackSuccessfulPaymentAnalytics } from '@/utils/paymentAnalytics';

const APPLE_PAY_CANCELLED_ERROR_NAME = 'ApplePayCancelledError';

function createApplePayCancelledError() {
  const error = new Error('Apple Pay payment was cancelled.');
  error.name = APPLE_PAY_CANCELLED_ERROR_NAME;
  return error;
}

function createPaymentFlowError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  const paymentError = new Error(getErrorMessage(error));

  if (error && typeof error === 'object') {
    const source = error as Record<string, unknown>;
    const paymentFields = ['code', 'type', 'decline_code'] as const;

    paymentFields.forEach((field) => {
      if (typeof source[field] === 'string') {
        Object.assign(paymentError, { [field]: source[field] });
      }
    });
  }

  return paymentError;
}

export function isApplePayCancelledError(error: unknown): boolean {
  return error instanceof Error && error.name === APPLE_PAY_CANCELLED_ERROR_NAME;
}

// Хук инкапсулирует всю логику подготовки и запуска оплаты триала через Apple Pay.
// Основные задачи:
// 1. Загрузить планы и выбрать актуальный trial план (weekly или первый доступный).
// 2. Сконфигурировать Stripe PaymentRequest, чтобы проверить наличие Apple Pay.
// 3. Показать лист Apple Pay и провести оплату, пробрасывая выбранный planId.
// 4. Передать результаты обратно во вью (через вычисленные свойства и методы).
export function useTrialPayment() {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isApplePayReady, setIsApplePayReady] = useState(false);
  const paymentRequestRef = useRef<PaymentRequest | null>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const paymentFlowPromiseRef = useRef<Promise<unknown> | null>(null);

  const trialPlans = subscriptionStore.trialPlans;
  const trialPlan = subscriptionStore.selectedTrialPlan;

  // Стоимость первичного списания. Используется в UI и для PaymentRequest.
  const trialPrice = useMemo(() => {
    if (!trialPlan) {
      return '0.00';
    }

    if (trialPlan.trial?.firstCharge) {
      return trialPlan.trial.firstCharge;
    }

    if (trialPlan.discount?.discountedPrice) {
      return trialPlan.discount.discountedPrice;
    }

    return trialPlan.price;
  }, [trialPlan]);

  // Публичный метод для выбора trial-плана из UI.
  const selectTrialPlan = useCallback((planId: string) => {
    subscriptionStore.setSelectedTrialPlan(planId);
  }, []);

  // Вспомогательный резолвер, который подстраховывает случаи, когда
  // selectedTrialPlan ещё не записан в стор (например, сразу после загрузки).
  const resolveTrialPlan = useCallback(
    (override?: PlanItem | null): PlanItem | null => {
      if (override) {
        return override;
      }
      return subscriptionStore.selectedTrialPlan ?? subscriptionStore.trialPlans[0] ?? null;
    },
    [],
  );

  // Detect device and check Apple Pay availability
  const preferredPaymentMethod = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    // Apple Pay only for iOS or macOS Safari
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isMacSafari
      = /Mac/.test(navigator.userAgent)
        && /Safari/.test(navigator.userAgent)
        && !/Chrome/.test(navigator.userAgent);

    const shouldUseApplePay = isIOS || isMacSafari;
    return shouldUseApplePay ? 'apple' : null;
  }, []);

  // Реконфигурирование Stripe PaymentRequest под конкретный trial-план.
  // Возвращает true, если Apple Pay доступен (canMakePayment -> applePay: true).
  const refreshPaymentRequest = useCallback(
    async (planOverride?: PlanItem | null) => {
      const currentPlan = resolveTrialPlan(planOverride);

      if (!currentPlan?.trial) {
        paymentRequestRef.current = null;
        setIsApplePayReady(false);
        return false;
      }

      const stripe = stripeRef.current;
      if (!stripe) {
        return false;
      }

      const firstChargeValue = Number.parseFloat(
        currentPlan.trial.firstCharge ?? currentPlan.price,
      );
      if (!Number.isFinite(firstChargeValue)) {
        console.error('Invalid trial first charge value');
        paymentRequestRef.current = null;
        setIsApplePayReady(false);
        return false;
      }

      const pr = stripe.paymentRequest({
        country: 'US',
        currency: (currentPlan.currency || 'usd').toLowerCase(),
        total: {
          label: 'Trial Verification',
          amount: Math.round(firstChargeValue * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      let canMakePayment;
      try {
        canMakePayment = await pr.canMakePayment();
      }
      catch (error) {
        console.error('Error checking Apple Pay availability:', error);
        paymentRequestRef.current = null;
        setIsApplePayReady(false);
        return false;
      }

      if (!canMakePayment || !canMakePayment.applePay) {
        paymentRequestRef.current = null;
        setIsApplePayReady(false);
        return false;
      }

      paymentRequestRef.current = pr;
      setIsApplePayReady(true);
      return true;
    },
    [resolveTrialPlan],
  );

  /**
   * Initialize Apple Pay in advance (called on component mount)
   */
  const initializeApplePay = useCallback(async () => {
    try {
      setIsInitializing(true);
      setIsApplePayReady(false);

      // Ensure plans are loaded before proceeding
      if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
        await subscriptionStore.fetchPlans();
      }

      const planForRequest = resolveTrialPlan();
      if (!planForRequest?.trial) {
        return false;
      }

      // Check if preferred payment method is available
      if (!preferredPaymentMethod) {
        return false;
      }

      // Get Stripe instance
      if (!stripeRef.current) {
        const stripe = await loadStripe(config.stripePublishableKey);
        if (!stripe) {
          return false;
        }
        stripeRef.current = stripe;
      }

      // Stripe готов, пробуем создать PaymentRequest и проверить доступность Apple Pay.
      const ready = await refreshPaymentRequest(planForRequest);
      return ready;
    }
    catch (error) {
      console.error('Error initializing Apple Pay:', error);
      setIsApplePayReady(false);
      return false;
    }
    finally {
      setIsInitializing(false);
    }
  }, [preferredPaymentMethod, refreshPaymentRequest, resolveTrialPlan]);

  /**
   * Handle payment error and return user-friendly message
   */
  const handlePaymentError = useCallback((error: unknown): string => {
    return getErrorMessage(error);
  }, []);

  /**
   * Show Apple Pay sheet (must be called on user click)
   * IMPORTANT: This function must call pr.show() IMMEDIATELY without async operations!
   */
  const showApplePay = useCallback(
    (meta?: string) => {
      if (paymentFlowPromiseRef.current) {
        return paymentFlowPromiseRef.current;
      }

      try {
        if (isInitializing) {
          throw new Error('Apple Pay is still initializing. Please wait a moment.');
        }

        if (!isApplePayReady || !paymentRequestRef.current) {
          throw new Error('Apple Pay is not ready. Please refresh the page.');
        }

        // Берём актуальный план ещё раз — на случай, если UI сменил его
        // между инициализацией и кликом пользователя.
        const currentPlan = resolveTrialPlan(trialPlan);
        if (!currentPlan?.trial) {
          throw new Error('No trial plan available');
        }

        const pr = paymentRequestRef.current;

        const paymentFlowPromise = new Promise((resolve, reject) => {
          let settled = false;
          let paymentStarted = false;
          let processingOverlayStarted = false;

          const cleanup = () => {
            pr.off('paymentmethod', handlePaymentMethod);
            pr.off('cancel', handleCancel);
          };

          const rejectOnce = (error: Error) => {
            if (settled) {
              return;
            }
            settled = true;
            cleanup();
            reject(error);
          };

          const resolveOnce = (result: unknown) => {
            if (settled) {
              return;
            }
            settled = true;
            cleanup();
            resolve(result);
          };

          const handleCancel = () => {
            if (paymentStarted || settled) {
              return;
            }
            rejectOnce(createApplePayCancelledError());
          };

          const handlePaymentMethod = async (ev: PaymentRequestPaymentMethodEvent) => {
            paymentStarted = true;
            pr.off('cancel', handleCancel);
            let eventCompleted = false;
            const completeEvent = (status: 'success' | 'fail') => {
              if (eventCompleted) {
                return;
              }
              eventCompleted = true;
              ev.complete(status);
            };

            try {
              // Get Stripe instance
              const stripe = stripeRef.current;
              if (!stripe) {
                completeEvent('fail');
                throw new Error('Stripe not configured');
              }

              // meta is encoded string - decode it only before sending to backend
              let decodedMeta: string | undefined = undefined;
              if (meta && typeof meta === 'string') {
                try {
                  const decodeResult = await apiStore.decodeLink(meta.trim());
                  if (decodeResult.ok && decodeResult.data) {
                    // Validate decoded meta is valid JSON with {str: str} structure
                    try {
                      const parsed = JSON.parse(decodeResult.data) as Record<string, unknown>;
                      if (
                        parsed
                        && typeof parsed === 'object'
                        && !Array.isArray(parsed)
                        && Object.values(parsed).every((v): v is string => typeof v === 'string')
                      ) {
                        decodedMeta = decodeResult.data;
                      }
                      else {
                        console.error('Invalid meta: must be {str: str}');
                      }
                    }
                    catch {
                      console.error('Invalid meta JSON');
                    }
                  }
                }
                catch {
                  console.error('Failed to decode meta');
                }
              }

              // Initialize subscription (matching HTML)
              // Передаём выбранный planId на backend, чтобы он зафиксировал правильный trial.
              const init = await apiStore.initSubscription({ planId: currentPlan.planId });

              if (!init.clientToken) {
                completeEvent('fail');
                throw new Error('Init failed: no client token received');
              }

              const clientSecret = init.clientToken;

              // Confirm card payment (matching HTML)
              const { error, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                { payment_method: ev.paymentMethod.id },
                { handleActions: false },
              );

              if (error) {
                completeEvent('fail');
                throw createPaymentFlowError(error);
              }

              completeEvent('success');
              paymentConfirmationStore.start();
              processingOverlayStarted = true;

              // Handle 3D Secure if required (matching HTML)
              if (paymentIntent.status === 'requires_action') {
                const { error: actErr } = await stripe.confirmCardPayment(clientSecret);
                if (actErr) {
                  throw createPaymentFlowError(actErr);
                }
              }

              // Confirm subscription: valid att_email, otherwise email from Apple Pay.
              const attParams = getStoredAttParams();
              const emailForConfirm = resolveAttributionEmail(
                attParams,
                ev.payerEmail,
              ) ?? '';
              const clickPayload = buildAttConversionClickPayload(attParams, emailForConfirm);
              if (clickPayload && !wasConversionClickSent(clickPayload)) {
                try {
                  await apiStore.conversionClick(clickPayload);
                  setConversionClickSent(clickPayload);
                }
                catch (error) {
                  console.warn('Не удалось отправить conversion/click перед confirmSubscription', error);
                }
              }

              const confirmResult = await apiStore.confirmSubscription({
                email: emailForConfirm,
                paymentData: { paymentIntentId: paymentIntent.id },
                meta: decodedMeta,
                planId: currentPlan.planId,
              });
              await trackSuccessfulPaymentAnalytics({
                paymentMethod: 'apple_pay',
                subscription: confirmResult.status,
                planId: currentPlan.planId,
              });
              // After successful payment, user is now authenticated, so fetch status
              await subscriptionStore.fetchStatus(true);

              // Отправляем сообщение об успешной оплате
              sendPaymentSuccess('applepay');

              const inWebView = isInWebView();
              const appsFlyerParams = inWebView
                ? null
                : consumeAppsFlyerParams();

              // Создаем handoff токен и делаем редирект на AppsFlyer deeplink
              const handoffResult = await apiStore.createHandoff();
              if (handoffResult.ok && handoffResult.token) {
                sendHandoff(handoffResult.token);

                if (inWebView) {
                  resolveOnce(confirmResult);
                  return;
                }

                // Получаем текущий путь (страница paywall)
                const currentPath = window.location.pathname;
                // Формируем диплинк с paywall версией из текущего пути
                const appsFlyerDeeplink = buildAppsFlyerDeeplinkFromPath(
                  currentPath,
                  {
                    attParams,
                    handoffToken: handoffResult.token,
                    paymentMethod: 'apple_pay',
                  },
                );
                const fallbackCampaign = appsFlyerDeeplink.paywallVersion
                  ? `paywall_${appsFlyerDeeplink.paywallVersion}`
                  : undefined;
                const redirectUrl = buildAppsFlyerRedirectUrl(
                  appsFlyerParams,
                  appsFlyerDeeplink,
                  fallbackCampaign,
                );
                navigateWithPaymentOverlay(redirectUrl);
                return;
              }

              if (inWebView) {
                resolveOnce(confirmResult);
                return;
              }

              resolveOnce(confirmResult);
            }
            catch (e) {
              completeEvent('fail');

              const errorMessage = handlePaymentError(e);

              // Отправляем сообщение об ошибке платежа
              sendPaymentFailed(errorMessage);

              rejectOnce(new Error(errorMessage));
            }
            finally {
              if (processingOverlayStarted) {
                paymentConfirmationStore.stopUnlessNavigating();
              }
            }
          };

          pr.once('paymentmethod', handlePaymentMethod);
          pr.once('cancel', handleCancel);

          try {
            // IMPORTANT: pr.show() must be called immediately from the user click path.
            // canMakePayment() уже был проверен при инициализации.
            pr.show();
          }
          catch (error) {
            const errorMessage = handlePaymentError(error);
            sendPaymentFailed(errorMessage);
            rejectOnce(new Error(errorMessage));
          }
        });

        paymentFlowPromiseRef.current = paymentFlowPromise;
        const clearPaymentFlow = () => {
          if (paymentFlowPromiseRef.current === paymentFlowPromise) {
            paymentFlowPromiseRef.current = null;
          }
        };
        paymentFlowPromise.then(clearPaymentFlow, clearPaymentFlow);

        return paymentFlowPromise;
      }
      catch (error) {
        console.error('Error showing Apple Pay:', error);

        // Отправляем сообщение об ошибке при инициализации Apple Pay
        const errorMessage = handlePaymentError(error);
        sendPaymentFailed(errorMessage);

        throw new Error(errorMessage);
      }
    },
    [handlePaymentError, isInitializing, isApplePayReady, resolveTrialPlan, trialPlan],
  );

  const startTrialWithOptimalPayment = useCallback(() => {
    return showApplePay();
  }, [showApplePay]);

  return {
    trialPlan,
    trialPlans,
    trialPrice,
    selectTrialPlan,
    preferredPaymentMethod,
    isInitializing,
    isApplePayReady,
    initializeApplePay,
    refreshPaymentRequest,
    startTrialWithOptimalPayment,
    showApplePay,
    handlePaymentError,
  };
}
